/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import '../../styles';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import textStyles from './sh-text.lit.scss.js';

/**
 * @prop {String} size - Default body-1. Sets the size of the text element. For available text sizes, see the 'Size' reference table under the 'Design' tab.
 * @prop {String} color - Default primary. Sets the color of the text element. secondary color can be set on title-2, body-1 , body-2 text sizes. If no color is specified, the default ones will be applied.
 * @prop {Boolean} disabled - Default false. If set to `true`, will update the text color and disable pointer events (in case of links).
 * @prop {String} href - Defines the destination link to be navigated to when the text element is clicked. Setting this value makes the text element into a link. This can only be defined for body-1 and body-2 sizes.
 */
export class SHText extends LitElement {
  static get properties() {
    return {
      size: { type: String, reflect: true },
      color: { type: String, reflect: true },
      href: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return textStyles;
  }

  render() {
    return this.href && (this.size === 'body-1' || this.size === 'body-2')
      ? html`<a href="${this.href}" tabindex=${this.disabled ? '-1' : '0'}><slot></slot></a>`
      : html`<slot></slot>`;
  }

  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
    this.dispatchEvent(new Event(`${name}-changed`));
  }

  constructor() {
    super();
    this.size = 'body-1';
    this.color = 'primary';
  }

  firstUpdated() {
    this.handleLegacyAttributes();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('no-hovermq');
    }
  }

  // remove default values for backwards compatibility
  handleLegacyAttributes() {
    let arr, self;
    arr = ['super-header', 'header', 'body-1', 'body-2', 'title-1', 'title-2'];
    self = this;
    arr.forEach((attr) => {
      if (self.hasAttribute(attr)) {
        console.warn(
          `You are using a legacy property (${attr}) of sh-text component. Please use the new 'size' and 'color' properties instead.`
        );
        if (self.size) {
          self.removeAttribute('size');
        }
      }
    });
  }
}

customElements.define('sh-text', SHText);
